import React, { useState, useEffect } from 'react';
import {
  Box, Paper, 
  TextField, 
  Typography, 
  Button,
  CircularProgress,
} from '@mui/material'
import 'chart.js/auto'
import { Pie, PolarArea } from 'react-chartjs-2'

import '../styles/css/profile.css'
import { get, put, post } from '../utils/http'
import UserIcon from '../assets/user-placeholder.svg'
import { bgGrey } from '../styles/js/colors.style'
import { LoadingAnimeSpinner } from '../component/common/Loaders'

interface UserFormData {
  title: string
  first_name: string,
  last_name: string,
  email: string,
  organisation: string,
}

interface SlideOverview {
  image_uploaded: number,
  shared_images: number,
  image_deleted: number,
  outcome_predictions_completed: number,
  gleason_gradings_completed: number,
  tiling_jobs_running: number,
  tissue_predictions_running: number
}

const ProfilePage = () => {
  const [slideOverview, setSlideOverview] = useState<SlideOverview | null>(null)
  const [profileEdit, setProfileEdit] = useState<boolean>(false)
  const [isLoading, seIsLoading] = useState<boolean>(true)
  const [isUserFormLoading, seIsUserFormLoading] = useState<boolean>(false)
  const [userFormReqMsg, setUserFormReqMsg] = useState<string>('')
  const [userFormData, setUserFormData] = useState<UserFormData>({
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    organisation: '',
  })

  const userId = Number(localStorage.getItem('user_id') || '')
  
  const fetchUserData = async () => {
    const res = await get(`Users/data/${userId}`)
    delete res['id']
    delete res['upload_limit']
    res.organisation = ''
    setUserFormData(res)
  }

  const fetchUserSlideOverview = async () => {
    const res = await get(`Users/slide-overview/${userId}`)
    setSlideOverview(res)
  }

  const fetchData = async () => {
    seIsLoading(true)
    try {
      await fetchUserData()
      await fetchUserSlideOverview()
    } catch (err) {
      console.log(err)
    } finally {
      seIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])  // eslint-disable-line

  const profileEditHandler = () => setProfileEdit(true)

  const userFormChangeHandler = (e: any) => {
    setUserFormData(prevState => ({...prevState, [e.target.name]: e.target.value}))
  }

  const profileFormCancelEdit = () => {
    setProfileEdit(false)
    setUserFormReqMsg('')
    fetchUserData()
  }

  const profileSaveHandler = async () => {
    seIsUserFormLoading(true)
    try {
      const res = await put(`Users/${userId}`, {
        title: userFormData.title,
        first_name: userFormData.first_name,
        last_name: userFormData.last_name
      })

      localStorage.setItem('fullName', `${res.title} ${res.first_name} ${res.last_name}`)
    } catch (err) {
      console.log(err)
    } finally {
      setProfileEdit(false)
      seIsUserFormLoading(false)
      setUserFormReqMsg('')
    }
  }

  const requestPasswordResetEmail = async () => {
    seIsUserFormLoading(true)
    try {
      const userEmail = localStorage.getItem('user_email')
      await post('Users/reset-password', {email: userEmail})
      setUserFormReqMsg('Password reset email generated')
    } catch (err) {
      console.log(err)
      setUserFormReqMsg('Password reset request failed')
    } finally {
      seIsUserFormLoading(false)
    }
  }

  return (
    <Box
      className='d-flex flex-wrap justify-content-center gap-3 p-3 w-100'
      sx={{maxWidth: 1200, margin: '0 auto', height: 'calc(100vh - 58px)'}}
    >
      {isLoading
        ? <LoadingAnimeSpinner />
        : <>
          <Paper elevation={3} className='flex-grow-1'>
            <Box className='d-flex flex-column gap-4 p-3'>
              <Typography variant="h5" component="h2">User Details</Typography>
              <Box id="p-usr-detail-box" className='d-flex gap-3 flex-wrap justify-content-between'>
                <Box className="d-flex justify-content-center align-items-center flex-grow-1" sx={{backgroundColor: bgGrey}}>
                  <img src={UserIcon} width={250} alt="user icon"/>
                </Box>
                <Box className='d-flex flex-column flex-grow-1 gap-3' component="form" noValidate autoComplete="off">
                  <TextField
                    label="Title"
                    variant="outlined"
                    name='title'
                    value={userFormData.title}
                    onChange={userFormChangeHandler}
                    disabled={!profileEdit}
                  />
                  <TextField
                    label="First Name"
                    variant="outlined"
                    name='first_name'
                    value={userFormData.first_name}
                    onChange={userFormChangeHandler}
                    disabled={!profileEdit}
                  />
                  <TextField
                    label="Last Name"
                    variant="outlined"
                    name='last_name'
                    value={userFormData.last_name}
                    onChange={userFormChangeHandler}
                    disabled={!profileEdit}
                  />
                  <TextField
                    label="Email"
                    name='email'
                    variant="outlined"
                    value={userFormData.email}
                    disabled
                  />
                  <TextField
                    label="Organisation"
                    name='organisation'
                    variant="outlined"
                    value={userFormData.organisation}
                    disabled
                  />
                  <div className='d-flex flex-column gap-3 align-items-end'>
                    {isUserFormLoading ? <CircularProgress size={35} />
                      : profileEdit ? <>
                        {userFormReqMsg.length ? <Typography>{userFormReqMsg}</Typography> : null}
                        <Button className='text-capitalize fs-6' onClick={requestPasswordResetEmail}>Change Password</Button>
                        <div className='d-flex gap-3'>
                          <Button className='text-capitalize fs-6' variant="outlined" sx={{width: 'fit-content'}} onClick={profileFormCancelEdit}>Cancel edit</Button>
                          <Button className='text-capitalize fs-6' variant="contained" sx={{width: 'fit-content'}} onClick={profileSaveHandler}>Save</Button>
                        </div>
                      </>
                      : <Button className='text-capitalize fs-6' variant="contained" sx={{width: 'fit-content'}} onClick={profileEditHandler}>Edit</Button>
                    }
                  </div>
                </Box>
              </Box>
            </Box>
          </Paper>
          <Paper elevation={3} className='flex-grow-1'>
            <Box className='d-flex flex-column gap-4 p-3'>
              <Typography variant="h5" component="h2">Slides Overview</Typography>
              <Box className='d-flex gap-3 flex-wrap justify-content-between'>
                <Box className='d-flex flex-column align-items-center gap-2' sx={{width: '100%', maxWidth: 500}}>
                  <Pie data={{
                    labels: ['Uploaded', 'Shared by Others'],
                    datasets: [{
                      label: 'Available Slides',
                      data: [slideOverview?.image_uploaded, slideOverview?.shared_images],
                      backgroundColor: ['rgb(255, 99, 132)', 'rgb(54, 162, 235)'],
                      hoverOffset: 4
                    }]}}
                  />
                  <Typography variant="caption">Available Slides</Typography>
                </Box>
                <Box className='d-flex flex-column align-items-center gap-2' sx={{width: '100%', maxWidth: 500}}>
                  <PolarArea data={{
                    labels: ['Outcome Prediction', 'Gleason Grading', 'Tissue Detection', 'Tiling'],
                    datasets: [{
                      label: 'Predictions Running',
                      data: [slideOverview?.outcome_predictions_completed, slideOverview?.gleason_gradings_completed,
                        slideOverview?.tissue_predictions_running, slideOverview?.tiling_jobs_running
                      ],
                      backgroundColor: ['rgb(255, 99, 132)', 'rgb(75, 192, 192)', 'rgb(255, 205, 86)', 'rgb(54, 162, 235)']
                    }]}}
                  />
                  <Typography variant="caption">Predictions Running</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </>
      }
    </Box>
  )
}

export default ProfilePage
